import { zodResolver } from '@hookform/resolvers/zod';
import GoogleIcon from '@mui/icons-material/Google';
import KeyIcon from '@mui/icons-material/Key';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import {
  Box,
  FormControlLabel,
  GlassAlert,
  GlassButton,
  GlassCheckbox,
  GlassTextField,
  Link,
  ShimmerLoader,
  Typography,
  useI18n,
  useTerminology,
} from '@ops-matrix/design-system-ui';
import { decodeJwtPayload, PORTAL_ALLOWED_ROLES } from '@ops-matrix/shared-types';
import {
  type AuthCredential,
  browserLocalPersistence,
  browserSessionPersistence,
  GoogleAuthProvider,
  getRedirectResult,
  type MultiFactorError,
  SAMLAuthProvider,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithRedirect,
} from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { env } from '@/config/env';
import { auth } from '../config/firebase';
import { useRedirect } from '../hooks/useRedirect';
import { useTheme } from '../theme/ThemeProvider';
import { FIREBASE_AUTH_ERROR, linkPendingCredential } from '../utils/account-linking';
import { isReturningFromFirebaseRedirect } from '../utils/firebase-redirect';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

/** SAML provider ID — only shown when explicitly configured via env var */
const SSO_PROVIDER_ID = env.VITE_SSO_PROVIDER_ID ?? null;

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { portalType } = useTheme();
  const { redirect: performRedirect } = useRedirect();
  const { t } = useI18n();
  const { term } = useTerminology();
  const platformName = term('navigation.platformName');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberDevice, setRememberDevice] = useState(true);
  // True on first render when we've just landed back from Google/SAML
  // signInWithRedirect — show a "Completing sign-in" view instead of the
  // form. Cleared in the getRedirectResult effect once the result resolves
  // (or fails), at which point we either navigate away or fall back to the
  // form for retry.
  const [completingRedirect, setCompletingRedirect] = useState<boolean>(isReturningFromFirebaseRedirect);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Populated when Google sign-in hits an email that already has password auth.
  // The password form then completes the sign-in and links the captured Google
  // credential so the user can use either method on their next visit.
  const [pendingLinkCredential, setPendingLinkCredential] = useState<AuthCredential | null>(null);
  const [pendingLinkEmail, setPendingLinkEmail] = useState<string | null>(null);

  const handleRedirect = useCallback(
    async (idToken: string) => {
      try {
        const response = await fetch(`${env.VITE_API_URL}/auth/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ firebaseIdToken: idToken }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[LoginPage] Token exchange failed:', errorText);
          throw new Error('Failed to exchange token');
        }

        const data = await response.json();

        // Defense-in-depth: validate user role matches requested portal before redirect
        if (portalType && Object.hasOwn(PORTAL_ALLOWED_ROLES, portalType)) {
          const payload = decodeJwtPayload<{ role?: string }>(data.accessToken);
          if (!payload) {
            console.error('[LoginPage] Failed to decode JWT for role check');
            setError(t('auth.portalAccessDenied'));
            return;
          }
          const allowedRoles = PORTAL_ALLOWED_ROLES[
            portalType as keyof typeof PORTAL_ALLOWED_ROLES
          ] as readonly string[];
          if (payload.role && !allowedRoles.includes(payload.role)) {
            console.warn(`[LoginPage] Role "${payload.role}" not allowed for portal "${portalType}"`);
            setError(t('auth.portalAccessDenied'));
            return;
          }
        }

        performRedirect(data.accessToken);
      } catch (err) {
        setError(t('auth.tokenExchangeFailed'));
        console.error('[LoginPage] Token exchange error:', err);
      }
    },
    [performRedirect, portalType, t],
  );

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await setPersistence(auth, rememberDevice ? browserLocalPersistence : browserSessionPersistence);

      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);

      // Account linking: if the user got here because Google sign-in hit an
      // existing password account, attach the captured Google credential
      // before handing off to the platform token exchange.
      if (pendingLinkCredential) {
        const result = await linkPendingCredential(userCredential.user, pendingLinkCredential);
        setPendingLinkCredential(null);
        setPendingLinkEmail(null);
        if (!result.success) setError(t(result.i18nKey));
      }

      const idToken = await userCredential.user.getIdToken();
      await handleRedirect(idToken);
    } catch (err) {
      const firebaseError = err as { code?: string };

      if (firebaseError.code === FIREBASE_AUTH_ERROR.MFA_REQUIRED) {
        const mfaError = err as MultiFactorError;
        navigate('/mfa/verify', {
          state: {
            mfaError,
            email: data.email,
            // Forward the pending link across MFA — MfaVerifyPage will attach
            // it once the second factor resolves.
            pendingLinkCredential,
          },
        });
        return;
      }

      if (firebaseError.code === FIREBASE_AUTH_ERROR.INVALID_CREDENTIAL) {
        setError(t('auth.invalidCredentials'));
      } else if (firebaseError.code === FIREBASE_AUTH_ERROR.TOO_MANY_REQUESTS) {
        setError(t('auth.tooManyAttempts'));
      } else {
        setError(t('auth.genericError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Process the redirect-based sign-in result on every page mount.
   *
   * Firebase's `signInWithRedirect` lands the user back on this page with the
   * OAuth result attached to the auth state. `getRedirectResult()` reads
   * exactly once per redirect; on a fresh load with no pending redirect it
   * returns null and we no-op. This is the official remediation path for the
   * Chrome 127+ / Firefox third-party-cookie blocking that broke
   * `signInWithPopup` — the popup variant relied on a hidden iframe at
   * `<project>.firebaseapp.com` and a third-party cookie, both of which are
   * now blocked by default in modern browsers. Redirect uses top-level
   * navigation only.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (cancelled) return;
        if (!result) {
          // No pending redirect (fresh page load) — drop the loader and
          // render the form. Safe to call unconditionally; setState is a
          // no-op when the value is already false on a fresh load.
          setCompletingRedirect(false);
          return;
        }
        const idToken = await result.user.getIdToken();
        await handleRedirect(idToken);
        // handleRedirect navigates away on success. If it returned without
        // navigating (role mismatch, exchange failure), drop the loader so
        // the user sees the error alert above the form.
        if (!cancelled) setCompletingRedirect(false);
      } catch (err) {
        if (cancelled) return;
        const firebaseError = err as { code?: string; customData?: { email?: string } };

        if (firebaseError.code === FIREBASE_AUTH_ERROR.MFA_REQUIRED) {
          const mfaError = err as MultiFactorError;
          const mfaErrorEmail = (mfaError.customData as { email?: string })?.email ?? '';
          navigate('/mfa/verify', { state: { mfaError, email: mfaErrorEmail } });
          return;
        }

        if (firebaseError.code === FIREBASE_AUTH_ERROR.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL) {
          const credential = GoogleAuthProvider.credentialFromError(
            err as Parameters<typeof GoogleAuthProvider.credentialFromError>[0],
          );
          const email = firebaseError.customData?.email ?? '';
          if (credential && email) {
            setPendingLinkCredential(credential);
            setPendingLinkEmail(email);
            form.setValue('email', email);
            setCompletingRedirect(false);
            return;
          }
        }

        setError(t('auth.googleSignInFailed'));
        setCompletingRedirect(false);
        console.error('Google redirect-result error:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally empty deps — getRedirectResult must run exactly once per
    // mount. Including `t` / `navigate` / `form` / `handleRedirect` causes
    // the effect to re-run when test mocks (or any reactive context) hand
    // back fresh references on every render, which would re-trigger the
    // redirect handler and re-set pendingLinkCredential after the user
    // already cleared it via successful password sign-in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await setPersistence(auth, rememberDevice ? browserLocalPersistence : browserSessionPersistence);
      const provider = new GoogleAuthProvider();
      // signInWithRedirect navigates the top-level window to Google's OAuth
      // endpoint and back. The result is consumed by the useEffect above on
      // the next page mount via getRedirectResult().
      await signInWithRedirect(auth, provider);
      // Execution does not continue past this line — the browser navigates
      // away. No need to reset isLoading; the redirect handles it.
    } catch (err) {
      // The only errors that surface synchronously here are pre-redirect
      // failures (config, persistence, network). MFA and account-linking
      // errors land in the redirect-result handler above.
      setError(t('auth.googleSignInFailed'));
      console.error('Google sign-in (pre-redirect) error:', err);
      setIsLoading(false);
    }
  };

  const handleSsoSignIn = async () => {
    if (!SSO_PROVIDER_ID) return;
    setIsLoading(true);
    setError(null);

    try {
      await setPersistence(auth, rememberDevice ? browserLocalPersistence : browserSessionPersistence);
      const provider = new SAMLAuthProvider(SSO_PROVIDER_ID);
      await signInWithRedirect(auth, provider);
    } catch (err) {
      setError(t('auth.ssoSignInFailed'));
      console.error('SSO sign-in (pre-redirect) error:', err);
      setIsLoading(false);
    }
  };

  const registerPath = portalType === 'consumer' ? '/register/consumer' : '/register';
  const currentParams = searchParams.toString();
  const queryString = currentParams ? `?${currentParams}` : '';

  if (completingRedirect) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.5,
          minHeight: 240,
          py: 4,
        }}
      >
        <ShimmerLoader variant="circular" width={40} height={40} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('auth.completingSignIn')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', maxWidth: 320 }}>
          {t('auth.completingSignInSubtext')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Page header (Welcome back + subtitle) */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.75 }}>
          {t('auth.signIn.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {t('auth.signIn.subtitle', { platformName })}
        </Typography>
      </Box>

      <GlassAlert severity="error" visible={!!error} sx={{ mb: 2 }}>
        {error}
      </GlassAlert>

      <GlassAlert severity="info" visible={!!pendingLinkCredential && !!pendingLinkEmail} sx={{ mb: 2 }}>
        {t('auth.accountExistsLinkGoogle', { email: pendingLinkEmail ?? '' })}
      </GlassAlert>

      <Box component="form" onSubmit={form.handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Email field */}
        <GlassTextField
          {...form.register('email')}
          label={t('common.email')}
          type="email"
          autoComplete="email"
          autoFocus
          disabled={isLoading}
          error={!!form.formState.errors.email}
          helperText={form.formState.errors.email?.message}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Password field with inline "Forgot password?" link */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {t('common.password')}
          </Typography>
          <Link
            component={RouterLink}
            to={`/forgot-password${queryString}`}
            variant="caption"
            sx={{ color: 'success.main' }}
          >
            {t('auth.forgotPassword')}
          </Link>
        </Box>
        <GlassTextField
          {...form.register('password')}
          type="password"
          autoComplete="current-password"
          disabled={isLoading}
          error={!!form.formState.errors.password}
          helperText={form.formState.errors.password?.message}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Remember device checkbox */}
        <FormControlLabel
          control={
            <GlassCheckbox
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('auth.signIn.rememberDevice')}
            </Typography>
          }
          sx={{ mb: 2.5 }}
        />

        {/* Primary sign in button */}
        <GlassButton type="submit" variant="contained" color="success" fullWidth size="large" disabled={isLoading}>
          {isLoading ? <ShimmerLoader variant="circular" width={24} height={24} /> : t('auth.signIn')}
        </GlassButton>
      </Box>

      {/* Mono divider "OR CONTINUE WITH" */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2.5 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography
          variant="overline"
          sx={{
            color: 'text.disabled',
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
            letterSpacing: '0.1em',
          }}
        >
          {t('auth.signIn.orContinueWith')}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* SSO buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <GlassButton
          variant="subtle"
          fullWidth
          size="large"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleSignIn}
          disabled={isLoading}
        >
          {t('auth.continueWithGoogle')}
        </GlassButton>

        {/*
          Magic-link is the BORROWER (consumer) flow — it hardcodes
          consumerType: 'BORROWER' and would log a business user into the
          consumer system. Hide it on the business + admin portals so loan
          officers don't accidentally land in the wrong namespace. Surfaces
          on portalType === 'consumer' or when no portal is specified
          (default consumer-style sign-in).
        */}
        {portalType !== 'business' && portalType !== 'admin' && (
          <GlassButton
            onClick={() => navigate(`/magic-link${queryString}`)}
            variant="subtle"
            fullWidth
            size="large"
            startIcon={<LinkIcon sx={{ color: 'success.main' }} />}
          >
            {t('auth.signIn.magicLinkLabel')}
          </GlassButton>
        )}

        {SSO_PROVIDER_ID && (
          <GlassButton
            variant="subtle"
            fullWidth
            size="large"
            startIcon={<KeyIcon sx={{ color: 'success.main' }} />}
            onClick={handleSsoSignIn}
            disabled={isLoading}
          >
            {t('auth.signIn.samlLabel')}
          </GlassButton>
        )}
      </Box>

      {/* Footer: request-access link */}
      <Box sx={{ mt: 3.5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('auth.signIn.footerNoAccount')}{' '}
          <Link
            component={RouterLink}
            to={`${registerPath}${queryString}`}
            sx={{ color: 'success.main', fontWeight: 600 }}
          >
            {t('auth.signIn.footerRequestAccess')} →
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
